// API client for flashcard backend
import { Card, CreateCardDTO, StudyNextResponse, AnswerDTO, ApiErrorClient } from 'shared';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Use the shared ApiError class
const ApiError = ApiErrorClient;

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add auth token from auth store if available
    const { token } = useAuthStore.getState();
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
      console.log('ApiClient: Adding auth token from store');
      console.log('ApiClient: Token length:', token.length);
    } else {
      console.log('ApiClient: No auth token available in store');
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle auth errors by clearing invalid tokens
        if (response.status === 401 && errorData.action === 'REAUTHENTICATE') {
          console.log('ApiClient: Auth error, clearing store and redirecting to login');
          useAuthStore.getState().logout();
          // Could redirect to login page here if needed
        }
        
        throw new ApiError({
          message: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
        });
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError({
        message: 'Network error or invalid JSON response',
        status: 0,
      });
    }
  }

  // Study endpoints
  async getNextCard(): Promise<StudyNextResponse> {
    return this.request<StudyNextResponse>('/api/study/next');
  }

  async submitAnswer(answer: AnswerDTO): Promise<{ success: boolean; card: Card }> {
    return this.request('/api/study/answer', {
      method: 'POST',
      body: JSON.stringify(answer),
    });
  }

  // Card management endpoints
  async getAllCards(): Promise<{ cards: Array<Card & { status: string; timeUntilReview: string }>; total: number }> {
    return this.request('/api/cards');
  }

  async getCard(id: string): Promise<{ card: Card }> {
    return this.request(`/api/cards/${id}`);
  }

  async createCard(cardData: CreateCardDTO): Promise<{ card: Card; message: string }> {
    return this.request('/api/cards', {
      method: 'POST',
      body: JSON.stringify(cardData),
    });
  }

  async updateCard(id: string, updates: Partial<Card>): Promise<{ card: Card; message: string }> {
    return this.request(`/api/cards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCard(id: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/cards/${id}`, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const api = new ApiClient();
export { api as apiClient } from './api';
